import { useTranslation } from "../../../../../hooks/useTranslation";
import { useCustom, useGetLocale } from "@refinedev/core";
import { GridColDef } from "@mui/x-data-grid";
import { useMemo } from "react";
import { AddCategoryButton } from "./AddCategoryButton";
import { useMarketplaceStore } from "../../../../../store/useMarketplaceStore";
import { PaginatedList } from "../../../../../components/form/PaginatedList/PaginatedList";

type RestrictedCategory = {
  id: number;
  parentCategoryName: string;
  categoryName: string;
  created: string;
};

export type Category = {
  id: string;
  name: string;
  children: Category[];
};

type CategoryResponse = {
  categories: Category[];
};

export const RestrictedCategories = () => {
  const { translate } = useTranslation(
    "pages.storeSecurity.restrictions.restrictedCategories",
  );

  const locale = useGetLocale();
  const currentLocale = locale();

  const columns = useMemo<GridColDef<RestrictedCategory>[]>(
    () => [
      {
        field: "parentCategoryName",
        headerName: translate("list.fields.parentCategory"),
        flex: 1,
        minWidth: 150,
        valueGetter: (value, row) => {
          if (!row.parentCategoryName) {
            return row.categoryName;
          }

          return value;
        },
      },
      {
        field: "categoryName",
        headerName: translate("list.fields.categoryName"),
        flex: 1,
        minWidth: 150,
        valueGetter: (value, row) => {
          if (!row.parentCategoryName) {
            return translate("list.fields.allSelected");
          }

          return value;
        },
      },
      {
        field: "created",
        headerName: translate("list.fields.createDate"),
        width: 200,
        valueFormatter: (value: string) => {
          const date = new Date(value).toLocaleDateString(currentLocale);
          const time = new Date(value).toLocaleTimeString(currentLocale);

          return `${date} ${time}`;
        },
      },
    ],
    [currentLocale],
  );

  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);

  const { data } = useCustom<CategoryResponse>({
    url: selectedCountry ? `marketplace/${selectedCountry.id}/category` : "",
    method: "get",
    queryOptions: {
      enabled: !!selectedCountry,
      queryKey: ["data"],
    },
  });

  return (
    <PaginatedList
      addButton={
        <AddCategoryButton categories={data?.data?.categories || []} />
      }
      getUrl={(countryId) => `store/${countryId}/restrictions/category`}
      columns={columns}
      searchLabel={translate("list.filters.search")}
    />
  );
};
